import { JourneyEvent } from "../api/analyticsAdmin";
import { parseEventProperties, SessionGroup } from "./analyticsPlus";
import { parseISO, isValid } from "date-fns";

export type EventCategory =
  | "search"
  | "business"
  | "outbound"
  | "auth"
  | "filter"
  | "navigation"
  | "other";

export interface EventCategoryInfo {
  category: EventCategory;
  label: string; // the raw event name (used as the timeline headline)
  color: string;
}

export const CATEGORY_META: Record<EventCategory, { label: string; color: string }> = {
  search: { label: "Search", color: "#6366f1" },
  business: { label: "Business", color: "#22c55e" },
  outbound: { label: "Outbound", color: "#f59e0b" },
  auth: { label: "Auth", color: "#8b5cf6" },
  filter: { label: "Filter", color: "#0ea5e9" },
  navigation: { label: "Navigation", color: "#a3a3a3" },
  other: { label: "Other", color: "#64748b" },
};

export const EVENT_CATEGORIES: Record<string, EventCategory> = {
  search_performed: "search",
  business_opened: "business",
  outbound_clicked: "outbound",
};

const FALLBACK_PALETTE = [
  "#0ea5e9",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#64748b",
];

const hashColor = (s: string): string => {
  let sum = 0;
  for (let i = 0; i < s.length; i++) {
    sum += s.charCodeAt(i);
  }
  return FALLBACK_PALETTE[sum % FALLBACK_PALETTE.length];
};

export const categorizeEvent = (eventName: string): EventCategoryInfo => {
  const category = EVENT_CATEGORIES[eventName];
  if (category) {
    return { category, label: eventName, color: CATEGORY_META[category].color };
  }
  return { category: "other", label: eventName, color: hashColor(eventName) };
};

export const formatDurationLong = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  if (total < 3600) {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}m ${s.toString().padStart(2, "0")}s`;
  }
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}h ${m.toString().padStart(2, "0")}m`;
};

export const eventPreview = (propertiesJson: string, max = 3): string => {
  const props = parseEventProperties(propertiesJson);
  const entries = Object.entries(props).slice(0, max);
  if (entries.length === 0) return "";
  return entries
    .map(([key, value]) => `${key}: ${typeof value === "object" ? JSON.stringify(value) : String(value)}`)
    .join(" · ");
};

export interface CollapsedRow {
  key: string;
  event: string;
  category: EventCategoryInfo;
  firstTimestamp: string;
  count: number;
  events: JourneyEvent[];
}

export const collapseConsecutive = (events: JourneyEvent[]): CollapsedRow[] => {
  const rows: CollapsedRow[] = [];
  events.forEach((event) => {
    const last = rows[rows.length - 1];
    if (last && last.event === event.event) {
      last.count += 1;
      last.events.push(event);
    } else {
      rows.push({
        key: event.uuid,
        event: event.event,
        category: categorizeEvent(event.event),
        firstTimestamp: event.timestamp,
        count: 1,
        events: [event],
      });
    }
  });
  return rows;
};

export const mergeJourneys = (eventArrays: JourneyEvent[][]): JourneyEvent[] =>
  eventArrays
    .flat()
    .slice()
    .sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0));

export type PersonIndex = Map<string, string[]>;

export const buildPersonIndex = (
  rows: { distinctId: string; personId: string | null }[]
): PersonIndex => {
  const index: PersonIndex = new Map();
  rows.forEach((row) => {
    if (!row.personId) return;
    const ids = index.get(row.personId) ?? [];
    if (!ids.includes(row.distinctId)) ids.push(row.distinctId);
    index.set(row.personId, ids);
  });
  return index;
};

export const resolveDistinctIds = (
  personId: string | null,
  clickedDistinctId: string,
  index: PersonIndex
): string[] => {
  if (personId) {
    const ids = index.get(personId);
    if (ids && ids.length > 0) {
      return ids.includes(clickedDistinctId) ? ids : [clickedDistinctId, ...ids];
    }
  }
  return [clickedDistinctId];
};

export interface TypeCount {
  category: EventCategory;
  label: string;
  color: string;
  count: number;
}

export interface SessionSummary {
  sessionId: string;
  start: string;
  end: string;
  durationSeconds: number;
  eventCount: number;
  bounce: boolean;
  queries: { query: string; totalCount: number | null }[];
  businessesOpened: number;
  outboundClicks: number;
  outboundDestinations: string[];
  typeCounts: TypeCount[];
  platform: string | null;
  oneLine: string;
}

const CATEGORY_ORDER: EventCategory[] = ["search", "business", "outbound", "auth", "filter", "navigation", "other"];

const pickString = (
  props: Record<string, unknown>,
  keys: string[]
): string | null => {
  for (const key of keys) {
    const value = props[key];
    if (value !== undefined && value !== null) return String(value);
  }
  return null;
};

export const summarizeSession = (group: SessionGroup): SessionSummary => {
  const events = group.events;
  if (events.length === 0) {
    return {
      sessionId: group.sessionId,
      start: "",
      end: "",
      durationSeconds: 0,
      eventCount: 0,
      bounce: false,
      queries: [],
      businessesOpened: 0,
      outboundClicks: 0,
      outboundDestinations: [],
      typeCounts: [],
      platform: null,
      oneLine: "0 events",
    };
  }
  const start = events[0].timestamp;
  const end = events[events.length - 1].timestamp;
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  const durationSeconds =
    isValid(startDate) && isValid(endDate)
      ? Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 1000))
      : 0;

  const queries: { query: string; totalCount: number | null }[] = [];
  let businessesOpened = 0;
  let outboundClicks = 0;
  const outboundDestinations: string[] = [];
  const catCounts = new Map<EventCategory, number>();
  let platform: string | null = null;

  events.forEach((event) => {
    const props = parseEventProperties(event.propertiesJson);
    if (platform === null) {
      platform = pickString(props, ["platform", "environment", "os"]);
    }
    const category = categorizeEvent(event.event).category;
    catCounts.set(category, (catCounts.get(category) ?? 0) + 1);

    if (event.event === "search_performed") {
      const query = pickString(props, ["query"]);
      if (query !== null) {
        const totalCount = props["total_count"];
        queries.push({
          query,
          totalCount: typeof totalCount === "number" ? totalCount : null,
        });
      }
    } else if (event.event === "business_opened") {
      businessesOpened += 1;
    } else if (event.event === "outbound_clicked") {
      outboundClicks += 1;
      const destination = pickString(props, ["url", "destination", "href"]);
      if (destination) outboundDestinations.push(destination);
    }
  });

  const typeCounts: TypeCount[] = CATEGORY_ORDER.filter((c) => catCounts.has(c)).map(
    (c) => ({
      category: c,
      label: CATEGORY_META[c].label,
      color: CATEGORY_META[c].color,
      count: catCounts.get(c) as number,
    })
  );

  let oneLine: string;
  if (queries.length > 0) {
    oneLine = `Searched "${queries[0].query}"${queries.length > 1 ? ` +${queries.length - 1}` : ""}`;
    if (businessesOpened > 0) oneLine += ` · opened ${businessesOpened}`;
  } else if (businessesOpened > 0) {
    oneLine = `Opened ${businessesOpened} business${businessesOpened > 1 ? "es" : ""}`;
  } else if (outboundClicks > 0) {
    oneLine = `${outboundClicks} outbound click${outboundClicks > 1 ? "s" : ""}`;
  } else {
    oneLine = `${events.length} event${events.length > 1 ? "s" : ""}`;
  }

  return {
    sessionId: group.sessionId,
    start,
    end,
    durationSeconds,
    eventCount: events.length,
    bounce: events.length === 1,
    queries,
    businessesOpened,
    outboundClicks,
    outboundDestinations,
    typeCounts,
    platform,
    oneLine,
  };
};
